import { Component, EventEmitter, Input, Output } from '@angular/core';

/** Legacy icon names mapped to Material Symbols Rounded ligatures. */
const GLYPHS: Record<string, string> = {
  inbox: 'inbox',
  search: 'search_off',
  contacts: 'person',
  campaigns: 'campaign',
  messages: 'sms',
  tags: 'sell',
  segments: 'donut_large',
  organizations: 'domain',
  reports: 'monitoring',
  users: 'group',
  schedule: 'schedule',
};

/**
 * Calm empty state: icon, one line, optional hint and actions.
 *
 *   <app-empty-state icon="contacts" message="No contacts yet" />
 *
 * Filter-aware: pass the live search term as `query` (and/or `filtered`
 * when filters are on) and the copy switches to "Nothing matches …"; bind
 * `(clear)` to get a Clear search / Clear filters button. `hint` is for the
 * true-empty case only; while filtered the built-in advice shows unless
 * `filteredHint` is set. `actionLabel` + `(action)` adds a primary button;
 * anything projected is placed with the actions. Always pass a specific
 * `message` for the true-empty case (e.g. "No contacts yet").
 */
@Component({
  selector: 'app-empty-state',
  standalone: true,
  imports: [],
  templateUrl: './empty-state.component.html',
  styleUrl: './empty-state.component.css',
})
export class EmptyStateComponent {
  @Input() message: string = 'Nothing here yet';
  @Input() icon: string = 'inbox'; // Options: 'inbox', 'search', 'contacts', 'campaigns', 'messages', 'tags', 'segments', 'organizations', 'reports', 'users', 'schedule', or any Material Symbols name
  @Input() size: 'small' | 'medium' | 'large' = 'medium';
  /** A second, quieter line: what to do next. Shown when nothing is filtered. */
  @Input() hint: string = '';
  /** Replaces the built-in advice under "Nothing matches …" while filtered. */
  @Input() filteredHint: string = '';
  /** The current search term. When set, the copy says nothing matches it. */
  @Input() query: string | null | undefined = '';
  /** True when filters (status, date…) narrow the list. */
  @Input() filtered = false;
  /** Label for a primary action button, e.g. "Compose message". */
  @Input() actionLabel: string = '';
  /** Material Symbols name shown in the action button. */
  @Input() actionIcon: string = '';

  @Output() action = new EventEmitter<void>();
  /** Clears the search and filters. The button shows only when this is bound. */
  @Output() clear = new EventEmitter<void>();

  get term(): string {
    return (this.query ?? '').toString().trim();
  }

  get isFiltered(): boolean {
    return !!this.term || this.filtered;
  }

  get glyph(): string {
    if (this.isFiltered) return this.term ? 'search_off' : 'filter_alt_off';
    return GLYPHS[this.icon] ?? (this.icon || 'inbox');
  }

  get line(): string {
    if (this.term) return `Nothing matches “${this.term}”`;
    if (this.filtered) return 'Nothing matches these filters';
    return this.message;
  }

  get subline(): string {
    if (!this.isFiltered) return this.hint;
    if (this.filteredHint) return this.filteredHint;
    if (this.term && this.filtered) return 'Try another search, or clear the filters.';
    if (this.term) return 'Check the spelling or try a shorter search.';
    return 'Clear the filters to see everything.';
  }

  get showClear(): boolean {
    return this.isFiltered && this.clear.observed;
  }

  get clearLabel(): string {
    return this.term && !this.filtered ? 'Clear search' : 'Clear filters';
  }
}
